import React, { useMemo, useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, money } from "@/components/common";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LoadBreakdownView, loadRpm, loadTotalMiles } from "@/components/LoadBreakdown";
import { MapPin, User, Clock, Warning, CloudSnow, MagnifyingGlass, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLUMNS = [
  ["Pre-Booked", "#94a3b8"], ["Dispatched", "#60a5fa"], ["Arrived at Shipper", "#818cf8"], ["In Transit", "#f59e0b"],
  ["Arrived at Receiver", "#14b8a6"], ["Delivered", "#10b981"],
];
const ALL_STATUSES = [...COLUMNS.map((c) => c[0]), "Cancelled", "TONU"];
const norm = (s) => ({ pending: "Pre-Booked", active: "In Transit", completed: "Delivered", delivered: "Delivered" }[s] || s || "Pre-Booked");
const city = (a) => (a || "").split(",").slice(-2).join(",").trim() || "—";

function LoadCard({ l, carrierName, onStatus, onView }) {
  const late = l.delivery_date && norm(l.status) !== "Delivered" && l.delivery_date < new Date().toISOString().slice(0, 10);
  return (
    <div className={`rounded-xl border bg-white p-3.5 shadow-sm hover:shadow-md transition-shadow ${late ? "border-red-300" : "border-slate-200"}`} data-testid={`board-card-${l.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono font-bold text-sm text-blue-600">{l.reference || "—"}</span>
        <button onClick={() => onView(l)} className="p-1 text-slate-400 hover:text-blue-600" data-testid={`board-view-${l.id}`}><Eye size={15} /></button>
      </div>
      <p className="text-[11px] text-slate-500 truncate">{carrierName}</p>
      <div className="mt-2 text-sm text-slate-900 font-medium leading-tight"><MapPin size={12} className="inline text-emerald-500 mr-1" />{city(l.shipper_address || l.origin)}<br /><MapPin size={12} className="inline text-blue-600 mr-1" />{city(l.receiver_address || l.destination)}</div>
      <div className="mt-2 grid grid-cols-2 gap-1 text-[11px] text-slate-500">
        <span><Clock size={11} className="inline mr-0.5" />PU {l.pickup_date || "—"} {l.pickup_time || ""}</span>
        <span className={late ? "text-red-600 font-semibold" : ""}><Clock size={11} className="inline mr-0.5" />DEL {l.delivery_date || "—"}</span>
        <span><User size={11} className="inline mr-0.5" />{l.driver || <b className="text-amber-600">No driver</b>}</span>
        <span>{loadTotalMiles(l) || 0} mi</span>
      </div>
      <div className="mt-2.5 flex items-center justify-between">
        <span className="font-mono font-bold text-slate-900">{money(l.rate)}</span>
        <span className="font-mono text-xs text-slate-500">{loadRpm(l) ? `$${loadRpm(l)}/mi` : ""}</span>
      </div>
      <Select value={ALL_STATUSES.includes(norm(l.status)) ? norm(l.status) : undefined} onValueChange={(v) => onStatus(l, v)}>
        <SelectTrigger className="mt-2.5 h-8 text-xs bg-slate-50 border-slate-200" data-testid={`board-status-${l.id}`}><SelectValue placeholder="Move to…" /></SelectTrigger>
        <SelectContent>{ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function WeatherWarnings() {
  const { data: warnings = [] } = useList("load-weather-warnings", "/loads/weather-warnings", { refetchInterval: 600000 });
  if (!warnings.length) return null;
  return (
    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="lane-weather-warnings">
      <div className="flex items-center gap-2 mb-2">
        <CloudSnow size={20} weight="duotone" className="text-amber-600" />
        <p className="font-heading font-bold text-amber-800">Lane weather alerts ({warnings.length})</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {warnings.map((w, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-amber-200 rounded-full px-3 py-1 text-xs text-amber-700" data-testid={`weather-warning-${w.load_id}`}>
            <Warning size={13} weight="fill" />
            <span className="font-semibold">{w.reference}</span> · {w.condition} at {w.point} ({w.city}){w.date ? ` · ${w.date}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Dispatch() {
  const { data: loads = [], isLoading } = useList("loads", "/loads", { refetchInterval: 12000 });
  const { data: carriers = [] } = useList("carriers", "/carriers");
  const invalidate = useInvalidate();
  const [q, setQ] = useState("");
  const [carrierId, setCarrierId] = useState("all");
  const [viewing, setViewing] = useState(null);
  const cName = (id) => carriers.find((c) => c.id === id)?.carrier_name || "—";
  const filtered = useMemo(() => loads.filter((l) => (carrierId === "all" || l.carrier_id === carrierId)
    && (!q || `${l.reference} ${l.driver} ${l.shipper_name} ${l.receiver_name} ${l.origin} ${l.destination}`.toLowerCase().includes(q.toLowerCase()))), [loads, q, carrierId]);
  const today = new Date().toISOString().slice(0, 10);
  const stats = [
    ["On the Board", filtered.filter((l) => norm(l.status) !== "Delivered").length],
    ["In Transit", filtered.filter((l) => norm(l.status) === "In Transit").length],
    ["Picking Up Today", filtered.filter((l) => l.pickup_date === today).length],
    ["No Driver", filtered.filter((l) => !l.driver && !["Delivered", "Cancelled", "TONU"].includes(norm(l.status))).length],
    ["Gross On Board", money(filtered.filter((l) => !["Delivered", "Cancelled", "TONU"].includes(norm(l.status))).reduce((s, l) => s + (Number(l.rate) || 0), 0))],
  ];
  const setStatus = async (l, status) => {
    try { await api.put(`/loads/${l.id}`, { status }); toast.success(`${l.reference} → ${status}`); invalidate(["loads", "loads-map", "dashboard"]); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <PageHeader title="Dispatch Board" subtitle="Every live load by stage. Updates every 12 seconds." />
      <WeatherWarnings />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        {stats.map(([l, v], i) => <Panel key={l} className="p-4"><p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{l}</p><p className="font-heading font-extrabold text-2xl text-slate-900 mt-1 tabular" data-testid={`board-stat-${i}`}>{v}</p></Panel>)}
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative"><MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search load #, driver, shipper…" className="h-10 w-72 pl-9 pr-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="board-search" /></div>
        <Select value={carrierId} onValueChange={setCarrierId}>
          <SelectTrigger className="w-64 bg-white border-slate-300" data-testid="board-carrier"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Carriers</SelectItem>{carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.carrier_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {isLoading ? <p className="text-slate-400">Loading…</p> : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="dispatch-board">
          {COLUMNS.map(([status, color]) => {
            const col = filtered.filter((l) => norm(l.status) === status);
            return (
              <div key={status} className="flex-1 min-w-[230px] rounded-2xl bg-slate-100/70 p-2.5" data-testid={`board-col-${status}`}>
                <div className="flex items-center justify-between px-1.5 pb-2.5">
                  <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-600"><span className="w-2 h-2 rounded-full" style={{ background: color }} />{status}</span>
                  <span className="text-xs font-mono font-bold text-slate-500">{col.length}</span>
                </div>
                <div className="space-y-2.5 max-h-[65vh] overflow-y-auto">
                  {col.length === 0 ? <p className="text-xs text-slate-400 text-center py-6">No loads</p> : col.map((l) => <LoadCard key={l.id} l={l} carrierName={cName(l.carrier_id)} onStatus={setStatus} onView={setViewing} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Load {viewing?.reference}</DialogTitle><DialogDescription className="text-slate-500">{viewing && cName(viewing.carrier_id)}</DialogDescription></DialogHeader>
          {viewing && <LoadBreakdownView load={viewing} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
